/**
 * <Button />
 *
 * Design principles:
 *   * Accessible by default: semantic <button> element, focus-visible ring,
 *     48x48 minimum tap target per WCAG 2.5.5 AAA.
 *   * Variants drive visual weight, not color abstractions. We ship three
 *     because that's all a strict 3-color palette can support cleanly:
 *       - primary  -> solid brand red (call-to-action)
 *       - secondary -> solid black (inverted / less important CTAs)
 *       - outline  -> white with black border (tertiary)
 *       - ghost    -> text-only (destructive/cancel rarely)
 *   * The `loading` state replaces the label with a spinner AND disables
 *     the button so the user cannot double-submit.
 */
import * as React from "react";
import { Loader2 } from "lucide-react";
import { cn } from "../../lib/cn";

type Variant = "primary" | "secondary" | "outline" | "ghost";
type Size = "sm" | "md" | "lg";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  /** Optional leading icon element (rendered left of the label). */
  leadingIcon?: React.ReactNode;
  /** Optional trailing icon element. */
  trailingIcon?: React.ReactNode;
  /** If true, button takes the full available width. */
  fullWidth?: boolean;
}

const base =
  "inline-flex items-center justify-center gap-2 font-semibold tracking-tight " +
  "transition-colors duration-150 select-none rounded-xl " +
  "disabled:opacity-50 disabled:cursor-not-allowed " +
  "focus-visible:outline-none focus-visible:shadow-glow";

const variants: Record<Variant, string> = {
  primary:
    "bg-brand text-white hover:bg-brand-hover active:bg-brand-ink " +
    "shadow-[0_1px_0_rgba(0,0,0,0.04),0_1px_2px_rgba(143,10,22,0.25)]",
  secondary:
    "bg-ink text-white hover:bg-ink-soft active:bg-black",
  outline:
    "bg-white text-ink border border-surface-borderStrong hover:bg-surface-raised",
  ghost:
    "bg-transparent text-ink hover:bg-surface-sunken",
};

const sizes: Record<Size, string> = {
  sm: "h-10 px-4 py-2 text-sm btn-target",
  md: "h-12 px-6 py-2.5 text-base btn-target",
  lg: "h-14 px-7 py-3 text-lg btn-target",
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      loading = false,
      leadingIcon,
      trailingIcon,
      fullWidth,
      className,
      children,
      disabled,
      type = "button",
      ...rest
    },
    ref,
  ) => {
    const isDisabled = disabled || loading;
    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled}
        aria-busy={loading || undefined}
        className={cn(
          base,
          variants[variant],
          sizes[size],
          fullWidth && "w-full",
          className,
        )}
        {...rest}
      >
        {loading ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
            <span className="sr-only">Loading</span>
            {children}
          </>
        ) : (
          <>
            {leadingIcon ? <span aria-hidden="true">{leadingIcon}</span> : null}
            <span>{children}</span>
            {trailingIcon ? <span aria-hidden="true">{trailingIcon}</span> : null}
          </>
        )}
      </button>
    );
  },
);
Button.displayName = "Button";
